import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Content, Header, Page } from '@backstage/core-components';
import { llmProxyLocaldevKeyAdminReadPermission } from '@internal/plugin-llm-proxy-keys-common';
import { usePermission } from '@backstage/plugin-permission-react';
import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useLlmProxyKeysApi } from '../hooks';
import { KeyRevealDialog } from './KeyRevealDialog';

type LocaldevKeysPageProps = {
  embedded?: boolean;
};

export const LocaldevKeysPage = ({ embedded = false }: LocaldevKeysPageProps) => {
  const api = useLlmProxyKeysApi();
  const { allowed: adminAllowed } = usePermission({
    permission: llmProxyLocaldevKeyAdminReadPermission,
  });
  const [reloadTick, setReloadTick] = useState(0);
  const { value, loading, error } = useAsync(
    () => api.listLocaldevKeys(),
    [api, reloadTick],
  );
  const {
    value: adminKeys,
    loading: adminLoading,
    error: adminError,
  } = useAsync(
    () => (adminAllowed ? api.listAdminLocaldevKeys() : Promise.resolve([])),
    [api, adminAllowed, reloadTick],
  );
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState<{ title: string; key: string }>();

  const run = async (action: 'create' | 'rotate' | 'revoke') => {
    setBusy(true);
    try {
      if (action === 'revoke') {
        await api.revokeLocaldevKey();
        setReloadTick(tick => tick + 1);
        return;
      }
      const result =
        action === 'create'
          ? await api.createLocaldevKey()
          : await api.rotateLocaldevKey();
      setReveal({
        title: action === 'create' ? 'API key created' : 'API key rotated',
        key: result.key,
      });
      setReloadTick(tick => tick + 1);
    } finally {
      setBusy(false);
    }
  };

  const revokeAdminKey = async (email: string) => {
    setBusy(true);
    try {
      await api.revokeAdminLocaldevKey(email);
      setReloadTick(tick => tick + 1);
    } finally {
      setBusy(false);
    }
  };

  const existingKey = value?.[0];

  const body = (
    <>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{String(error)}</Alert>}
      {!loading && !error && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader title="Your localdev key" />
              <CardContent>
                {existingKey ? (
                  <>
                    <Typography paragraph>
                      Key name: <strong>{existingKey.name}</strong>
                    </Typography>
                    <Typography color="textSecondary" paragraph>
                      User ID: {existingKey.userId}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => run('rotate')}
                      disabled={busy}
                      style={{ marginRight: 8 }}
                    >
                      Rotate
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => run('revoke')}
                      disabled={busy}
                    >
                      Revoke
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography paragraph>
                      No personal API key yet for prod llm-proxy-localdev.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => run('create')}
                      disabled={busy}
                    >
                      Create key
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {adminAllowed && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="All localdev keys (admin)" />
                <CardContent>
                  {adminLoading && <CircularProgress />}
                  {adminError && (
                    <Alert severity="error">{String(adminError)}</Alert>
                  )}
                  {!adminLoading && !adminError && !adminKeys?.length && (
                    <Typography color="textSecondary">No keys yet.</Typography>
                  )}
                  {adminKeys?.map(item => (
                    <div key={item.name} style={{ marginBottom: 16 }}>
                      <Typography>
                        <strong>{item.name}</strong> (user_id: {item.userId})
                      </Typography>
                      <Button
                        size="small"
                        color="secondary"
                        onClick={() => revokeAdminKey(item.userId)}
                        disabled={busy}
                      >
                        Revoke
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
      {reveal && (
        <KeyRevealDialog
          open
          title={reveal.title}
          apiKey={reveal.key}
          message="Copy this key now and store it somewhere safe. It will not be shown again."
          onClose={() => setReveal(undefined)}
        />
      )}
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Page themeId="tool">
      <Header title="LLM Proxy Keys (Personal)" />
      <Content>{body}</Content>
    </Page>
  );
};
